/**
 * 공급 흐름도(flo) 색·타이포 토큰 — 렌더러·범례·썸네일이 같은 색과 글꼴을 쓰도록 한곳에 둔다.
 * 헥스가 MUI 팔레트 스웨치와 정확히 일치하는 자리는 @mui/material/colors 를 쓴다.
 * 일치하는 스웨치가 없는 값(matFlow·dot 의 회색선)만 헥스를 그대로 남긴다.
 * 중립 leaf — pages/** 를 import 하지 않는다.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  green, deepOrange, grey, blue, teal, blueGrey, amber, purple, common,
} from "@mui/material/colors";
import { getFontFamily, getFontSizePx } from "@plannel/utils/font-util";

// 고정 오버레이 치수 — FloViewOverlay.js(거터·눈금자)와 FloCanvas(맞춤·확대 중심)가 함께 쓴다.
export const FLO_GUTTER_W = 152;
export const FLO_RULER_H = 24;

export const FLO_COLORS = {
  canvasBg: common.white,
  laneBorder: grey[300],
  colGuide: grey[200],
  invFill: green[300], invStroke: green[800],
  invPurFill: common.white, invPurStroke: green[700],
  invRootFill: green[600], invRootStroke: green[900],
  invGapStroke: deepOrange[600], invGapFill: deepOrange[100],
  invUnknownFill: grey[200], invUnknownStroke: grey[500],
  invAggFill: grey[300], invAggStroke: grey[500],
  makeFill: blue[100], makeStroke: blue[600],
  kitFill: teal[50], kitStroke: teal[600],
  moveFill: blueGrey[50], moveStroke: blueGrey[400],
  resFill: amber[100], resStroke: amber[700],
  moreFill: grey[100], moreStroke: grey[500],
  // 팔레트에 정확히 맞는 스웨치가 없는 값 — 원본 헥스 그대로.
  matFlow: "#b1b1b7",
  moveFlow: blueGrey[400],
  backFlow: grey[500],
  chipQtyFill: green[100], chipQtyBorder: green[600],
  chipRatioFill: blue[50], chipRatioBorder: blue[600],
  text1: grey[800], text2: grey[600],
  dot: "#dfe3e8",
};

export const EDGE_COLOR = { make: FLO_COLORS.matFlow, kit: FLO_COLORS.matFlow, move: FLO_COLORS.moveFlow };

// 레인 유형별 부제 글자색 — MP FLO 배경과 같은 배정이다.
const TYPE_COLOR = {
  PLANT: green[700], DC: blue[700], WHSE: blue[700], VIRTUAL_DC: blue[700], STO: blue[700],
  VENDOR: amber[800], CUST: purple[700],
};

/** 레인 유형 글자색 — 모르는 유형은 중성 회색이다. */
export function typeColor(type) {
  return TYPE_COLOR[type] || grey[600];
}

// 레인 유형 -> 기존 menu i18n 키. 목록에 없는 유형은 코드 그대로 보인다.
export const LANE_TYPE_LABEL_KEY = {
  PLANT: "plant", DC: "dc", WHSE: "whse", STO: "sto", CUST: "cust", VENDOR: "vendor", VIRTUAL_DC: "virtualDc",
};

/**
 * 노드 라벨·코드·배지용 폰트 문자열.
 * canvas 측정에는 var() 가 아니라 해석된 문자열이 필요해 getFontFamily/getFontSizePx 를 쓴다.
 * 웹폰트가 늦게 뜨는 경우를 대비해 document.fonts.ready 이후 한 번 더 값을 계산한다.
 */
export function useFloFonts() {
  const { i18n } = useTranslation();
  const [epoch, setEpoch] = useState(0);
  useEffect(() => {
    let on = true;
    document.fonts?.ready?.then(() => { if (on) setEpoch((e) => e + 1); });
    return () => { on = false; };
  }, []);
  const family = getFontFamily(i18n.language);
  return useMemo(() => ({
    family,
    // 웹폰트 적재 세대 — 글꼴 문자열은 적재 전후가 같아, 측정값 캐시(useFlo)는 이 값으로 갈아 낀다.
    epoch,
    labelFont: `600 ${getFontSizePx("body")}px ${family}`,
    codeFont: `${getFontSizePx("caption")}px ${family}`,
    badgeFont: `700 ${getFontSizePx("caption")}px ${family}`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [family, i18n.language, epoch]);
}
